import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DomainStatus } from '@lizrd/contracts';
import { getDomain } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const STATUS_VARIANT: Record<DomainStatus, 'success' | 'warning' | 'neutral'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  ARCHIVED: 'neutral',
};

/**
 * The core page for an Engineering Domain — the hub where everything for the
 * domain connects. Navigation between its sub-pages lives in the left sidebar;
 * this page renders the active sub-page via <Outlet />.
 */
export function DomainDetailPage() {
  const { domainId = '' } = useParams();
  const domain = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
    enabled: !!domainId,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-2xl font-bold">
          {domain.isLoading ? <Skeleton className="h-7 w-44" /> : (domain.data?.name ?? 'Domain')}
        </h1>
        {domain.data && (
          <Badge variant={STATUS_VARIANT[domain.data.status]}>
            {domain.data.status.toLowerCase()}
          </Badge>
        )}
      </div>

      <Outlet />
    </div>
  );
}
